<template>
  <div class="tf td">
    <Breadcrumbs main="Тикеты" title="Дашборд поддержки"/>

    <div class="container-fluid tf-wrap">

      <!-- ======================= верхняя панель ======================= -->
      <div class="tf-topbar">
        <div class="tf-topbar__title">
          <div class="tf-topbar__name">Дашборд поддержки</div>
          <div class="tf-topbar__meta">
            <span>{{ activePeriodLabel }}</span>
            <i class="tf-dot"></i>
            <span v-if="updatedAt">обновлено в {{ updatedAt }}</span>
            <span v-else>загрузка…</span>
          </div>
        </div>

        <div class="tf-topbar__spacer"></div>

        <div class="tf-topbar__actions">
          <div class="td-seg" role="group" aria-label="Период">
            <button v-for="option in periods" :key="option.value" type="button"
                    class="td-seg__btn" :class="{ 'is-on': period === option.value }"
                    @click="setPeriod(option.value)">
              {{ option.label }}
            </button>
          </div>

          <button type="button" class="tf-icon-btn" :class="{ 'tf-spin': isAnyLoading }"
                  @click="loadAll" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <router-link :to="{ name: 'tickets_board' }" class="tf-btn-ghost">
            <i class="fa fa-columns"></i> Доска тикетов
          </router-link>
        </div>
      </div>

      <!-- ======================= метрики ======================= -->
      <div class="td-metrics">
        <div v-for="metric in metricsCards" :key="metric.key" class="td-metric" :class="'td-metric--' + metric.tone">
          <div class="td-metric__top">
            <span class="td-metric__title">{{ metric.title }}</span>
            <i class="fa td-metric__icon" :class="metric.icon"></i>
          </div>
          <div class="td-metric__value">
            <span v-if="metricsLoading" class="td-sk td-sk--value"></span>
            <template v-else>{{ metric.value }}</template>
          </div>
          <div class="td-metric__sub">{{ metric.subtitle }}</div>
        </div>
      </div>

      <!-- ======================= графики ======================= -->
      <div class="td-grid">

        <!-- Динамика обращений -->
        <section class="tf-card td-card--wide">
          <div class="tf-card__head">
            <span class="tf-card__icon"><i class="fa fa-line-chart"></i></span>
            <div class="tf-card__titles">
              <h3 class="tf-card__title">Динамика обращений</h3>
              <p class="tf-card__sub">Созданные, решённые и отклонённые тикеты {{ activePeriodLabel.toLowerCase() }}</p>
            </div>
            <div class="td-head-tools">
              <div class="td-seg td-seg--sm">
                <button v-for="option in groupByOptions" :key="option.value" type="button"
                        class="td-seg__btn" :class="{ 'is-on': groupBy === option.value }"
                        @click="setGroupBy(option.value)">
                  {{ option.text }}
                </button>
              </div>
              <button type="button" class="tf-icon-btn" :class="{ 'is-on': volumeAsTable }"
                      @click="volumeAsTable = !volumeAsTable"
                      v-b-tooltip.hover.top="volumeAsTable ? 'Показать графиком' : 'Показать таблицей'">
                <i class="fa" :class="volumeAsTable ? 'fa-area-chart' : 'fa-table'"></i>
              </button>
            </div>
          </div>
          <div class="tf-card__body">
            <div v-if="volumeLoading" class="td-plot td-plot--busy">
              <span class="td-sk td-sk--plot"></span>
            </div>
            <div v-else-if="!volumeData.length" class="td-empty">
              <i class="fa fa-line-chart"></i>
              <span>За выбранный период обращений не было</span>
            </div>
            <div v-else-if="volumeAsTable" class="td-table-wrap">
              <table class="td-table">
                <thead>
                <tr>
                  <th>Период</th>
                  <th class="td-num">Созданные</th>
                  <th class="td-num">Решённые</th>
                  <th class="td-num">Отклонённые</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in volumeData" :key="'v' + row.date">
                  <td>{{ row.date }}</td>
                  <td class="td-num">{{ row.created }}</td>
                  <td class="td-num">{{ row.resolved }}</td>
                  <td class="td-num">{{ row.rejected }}</td>
                </tr>
                </tbody>
              </table>
            </div>
            <apexchart v-else type="area" height="300" :key="'vol' + themeKey"
                       :options="volumeChartOptions" :series="volumeChartSeries"/>
          </div>
        </section>

        <!-- Источники -->
        <section class="tf-card">
          <div class="tf-card__head">
            <span class="tf-card__icon"><i class="fa fa-share-alt"></i></span>
            <div class="tf-card__titles">
              <h3 class="tf-card__title">Источники</h3>
              <p class="tf-card__sub">Откуда приходят обращения</p>
            </div>
            <button type="button" class="tf-icon-btn" :class="{ 'is-on': sourcesAsTable }"
                    @click="sourcesAsTable = !sourcesAsTable"
                    v-b-tooltip.hover.top="sourcesAsTable ? 'Показать графиком' : 'Показать таблицей'">
              <i class="fa" :class="sourcesAsTable ? 'fa-pie-chart' : 'fa-table'"></i>
            </button>
          </div>
          <div class="tf-card__body">
            <div v-if="sourcesLoading" class="td-plot td-plot--busy">
              <span class="td-sk td-sk--plot"></span>
            </div>
            <div v-else-if="!sourceRows.length" class="td-empty">
              <i class="fa fa-share-alt"></i>
              <span>Данных нет</span>
            </div>
            <div v-else-if="sourcesAsTable" class="td-table-wrap">
              <table class="td-table">
                <thead>
                <tr>
                  <th>Источник</th>
                  <th class="td-num">Тикетов</th>
                  <th class="td-num">Доля</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in sourceRows" :key="'s' + row.label">
                  <td>
                    <span class="td-swatch" :style="{ background: row.color }"></span>{{ row.label }}
                  </td>
                  <td class="td-num">{{ row.count }}</td>
                  <td class="td-num">{{ row.share }}%</td>
                </tr>
                </tbody>
              </table>
            </div>
            <apexchart v-else type="donut" height="300" :key="'src' + themeKey"
                       :options="sourcesChartOptions" :series="sourcesChartSeries"/>
          </div>
        </section>

        <!-- Категории -->
        <section class="tf-card">
          <div class="tf-card__head">
            <span class="tf-card__icon"><i class="fa fa-tags"></i></span>
            <div class="tf-card__titles">
              <h3 class="tf-card__title">Категории</h3>
              <p class="tf-card__sub">Топ тем обращений</p>
            </div>
            <b-dropdown variant="link" class="td-drop" no-caret right menu-class="td-drop__menu">
              <template #button-content>
                <span class="tf-badge" :class="{ 'tf-badge--primary': categoryTypeId !== null }">
                  {{ activeCategoryTypeLabel }} <i class="fa fa-angle-down"></i>
                </span>
              </template>
              <b-dropdown-item v-for="option in categoryTypeOptions" :key="'ct' + option.value"
                               @click="setCategoryType(option.value)">
                <i class="fa fa-check td-drop__check"
                   :class="{ 'is-hidden': categoryTypeId !== option.value }"></i>
                {{ option.text }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="tf-card__body">
            <div v-if="categoriesLoading" class="td-plot td-plot--busy">
              <span class="td-sk td-sk--plot"></span>
            </div>
            <div v-else-if="!categoriesData.length" class="td-empty">
              <i class="fa fa-tags"></i>
              <span>Категорий за период нет</span>
            </div>
            <div v-else class="td-bars">
              <div v-for="row in categoryRows" :key="'c' + row.name" class="td-bar">
                <div class="td-bar__top">
                  <span class="td-bar__name" :title="row.name">{{ row.name }}</span>
                  <span class="td-bar__value">{{ row.count }}</span>
                </div>
                <div class="td-bar__track">
                  <div class="td-bar__fill" :style="{ width: row.percent + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Производительность команды -->
        <section class="tf-card td-card--wide">
          <div class="tf-card__head">
            <span class="tf-card__icon"><i class="fa fa-users"></i></span>
            <div class="tf-card__titles">
              <h3 class="tf-card__title">Производительность команды</h3>
              <p class="tf-card__sub">Обработанные тикеты и решения по заявкам</p>
            </div>
            <div class="td-head-tools">
              <div class="td-search">
                <i class="fa fa-search"></i>
                <input type="search" v-model="teamSearch" placeholder="Сотрудник…">
              </div>
            </div>
          </div>
          <div class="tf-card__body tf-card__body--flush">
            <div v-if="teamLoading" class="td-plot td-plot--busy">
              <span class="td-sk td-sk--plot"></span>
            </div>
            <div v-else-if="!sortedTeamPerformance.length" class="td-empty">
              <i class="fa fa-users"></i>
              <span>{{ teamSearch ? 'Никого не нашлось' : 'Нет данных за выбранный период' }}</span>
            </div>
            <div v-else class="td-table-wrap">
              <table class="td-table td-table--hover">
                <thead>
                <tr>
                  <th class="td-th--sort" @click="toggleTeamSort('username')">
                    Сотрудник <span class="td-sort">{{ getSortIcon('username') }}</span>
                  </th>
                  <th class="td-num td-th--sort" @click="toggleTeamSort('processed')">
                    Обработано <span class="td-sort">{{ getSortIcon('processed') }}</span>
                  </th>
                  <th class="td-num td-th--sort" @click="toggleTeamSort('key_actions')">
                    Ключевые действия <span class="td-sort">{{ getSortIcon('key_actions') }}</span>
                  </th>
                  <th class="td-num td-th--sort" @click="toggleTeamSort('entries_accepted')">
                    Заявки команд <span class="td-sort">{{ getSortIcon('entries_accepted') }}</span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="agent in sortedTeamPerformance" :key="agent.user_id">
                  <td>
                    <div class="td-agent">
                      <span class="td-agent__avatar">{{ getInitial(agent.username) }}</span>
                      <span class="td-agent__name">{{ agent.username }}</span>
                    </div>
                  </td>
                  <td class="td-num">
                    <div class="td-load">
                      <b>{{ agent.processed }}</b>
                      <span class="td-load__track">
                        <span class="td-load__fill" :style="{ width: loadPercent(agent.processed) + '%' }"></span>
                      </span>
                    </div>
                  </td>
                  <td class="td-num">
                    <span class="tf-badge tf-badge--primary">{{ agent.key_actions }}</span>
                  </td>
                  <td class="td-num">
                    <span class="td-entries">
                      <b class="is-ok">{{ agent.entries_accepted }}</b>
                      <i>принято</i>
                      <span class="td-entries__sep">·</span>
                      <b class="is-no">{{ agent.entries_rejected }}</b>
                      <i>отклонено</i>
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/api"
import VueApexCharts from 'vue-apexcharts'

const SOURCE_LABELS = {
  website: 'Web-виджет',
  telegram: 'Telegram',
}

/**
 * Палитры прогнаны через валидатор доступности (OKLab ΔE под протан/дейтан,
 * контраст к поверхности карточки). Порядок слотов менять нельзя — он и есть
 * механизм различимости: зелёный и красный рядом сливаются при дальтонизме,
 * поэтому «отклонённые» — оранжевый, а не красный.
 */
const SERIES_COLORS = {
  light: ['#7366ff', '#199e70', '#d95926'],
  dark: ['#7366ff', '#1baf7a', '#d95926'],
}

const SOURCE_COLORS = {
  light: ['#7366ff', '#d95926', '#199e70'],
  dark: ['#7366ff', '#d95926', '#1baf7a'],
}

const NEUTRAL_COLOR = {light: '#9aa1ad', dark: '#6c7482'}

export default {
  name: "TicketsDashboard",
  components: {apexchart: VueApexCharts},
  data() {
    return {
      period: 'week',
      periods: [
        {value: 'day', label: 'День'},
        {value: 'week', label: 'Неделя'},
        {value: 'month', label: 'Месяц'},
        {value: 'quarter', label: 'Квартал'},
        {value: 'year', label: 'Год'},
      ],
      groupBy: 'day',
      groupByOptions: [
        {value: 'hour', text: 'По часам'},
        {value: 'day', text: 'По дням'},
      ],
      categoryTypeId: null,
      categoryTypeOptions: [{value: null, text: 'Все типы'}],

      metrics: null,
      volumeData: [],
      sourcesData: [],
      categoriesData: [],
      teamPerformance: [],

      teamSortKey: 'processed',
      teamSortAsc: false,
      teamSearch: '',

      volumeAsTable: false,
      sourcesAsTable: false,

      updatedAt: '',
      isDark: false,
      themeObserver: null,

      metricsLoading: false,
      volumeLoading: false,
      sourcesLoading: false,
      categoriesLoading: false,
      teamLoading: false,
    }
  },

  computed: {
    isAnyLoading() {
      return this.metricsLoading || this.volumeLoading || this.sourcesLoading
          || this.categoriesLoading || this.teamLoading;
    },

    themeKey() {
      return this.isDark ? 'dark' : 'light';
    },

    palette() {
      return {
        series: SERIES_COLORS[this.themeKey],
        sources: SOURCE_COLORS[this.themeKey],
        neutral: NEUTRAL_COLOR[this.themeKey],
        text: this.isDark ? '#98a6ad' : '#7b8496',
        grid: this.isDark ? '#374558' : '#e7eaf3',
      };
    },

    activePeriodLabel() {
      const option = this.periods.find(item => item.value === this.period);
      return option ? option.label : '';
    },

    activeCategoryTypeLabel() {
      const option = this.categoryTypeOptions.find(item => item.value === this.categoryTypeId);
      return option ? option.text : 'Все типы';
    },

    metricsCards() {
      const m = this.metrics;
      const dash = '—';
      return [
        {
          key: 'total', title: 'Всего обращений', icon: 'fa-inbox', tone: 'primary',
          value: m ? m.total_tickets : dash, subtitle: 'за период',
        },
        {
          key: 'act', title: 'Завершение (ACT)', icon: 'fa-clock-o', tone: 'neutral',
          value: m ? this.formatMinutes(m.act_minutes) : dash, subtitle: 'среднее время до закрытия',
        },
        {
          key: 'iat', title: 'Реакция (IAT)', icon: 'fa-bolt', tone: 'neutral',
          value: m ? this.formatMinutes(m.iat_minutes) : dash, subtitle: 'среднее время первого ответа',
        },
        {
          key: 'rejected', title: 'Отклонено', icon: 'fa-times-circle-o', tone: 'danger',
          value: m ? m.rejected : dash, subtitle: 'за период',
        },
        {
          key: 'mood', title: 'Тональность', icon: 'fa-smile-o', tone: 'ok',
          value: m && m.mood !== null && m.mood !== undefined ? m.mood.toFixed(1) : dash,
          subtitle: 'оценка от 1 до 5',
        },
      ];
    },

    // ---------------------------------------------- динамика обращений
    volumeChartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: {show: false},
          fontFamily: 'inherit',
          background: 'transparent',
          animations: {enabled: false},
        },
        theme: {mode: this.isDark ? 'dark' : 'light'},
        colors: this.palette.series,
        dataLabels: {enabled: false},
        stroke: {curve: 'smooth', width: 2},
        fill: {
          type: 'gradient',
          gradient: {shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 95, 100]},
        },
        markers: {size: 0, strokeWidth: 2, hover: {size: 5}},
        xaxis: {
          categories: this.volumeData.map(row => row.date),
          labels: {style: {colors: this.palette.text, fontSize: '11px'}},
          axisBorder: {show: false},
          axisTicks: {show: false},
          crosshairs: {stroke: {color: this.palette.grid, width: 1, dashArray: 3}},
          tooltip: {enabled: false},
        },
        yaxis: {labels: {style: {colors: this.palette.text, fontSize: '11px'}}},
        grid: {borderColor: this.palette.grid, strokeDashArray: 4, padding: {left: 4, right: 4}},
        legend: {position: 'top', horizontalAlign: 'left', markers: {radius: 3}, labels: {colors: this.palette.text}},
        tooltip: {shared: true, intersect: false, theme: this.isDark ? 'dark' : 'light'},
      };
    },

    volumeChartSeries() {
      return [
        {name: 'Созданные', data: this.volumeData.map(row => row.created)},
        {name: 'Решённые', data: this.volumeData.map(row => row.resolved)},
        {name: 'Отклонённые', data: this.volumeData.map(row => row.rejected)},
      ];
    },

    // ---------------------------------------------- источники
    /** Больше трёх источников палитра не различает — остальное сводим в «Другие». */
    sourceRows() {
      const raw = (this.sourcesData || []).map(row => ({
        label: SOURCE_LABELS[row.source] || row.source || 'Не указан',
        count: Number(row.count) || 0,
      }));
      const sorted = raw.slice().sort((a, b) => b.count - a.count);
      const colors = this.palette.sources;

      let rows = sorted;
      if (sorted.length > colors.length) {
        const head = sorted.slice(0, colors.length - 1);
        const tail = sorted.slice(colors.length - 1);
        rows = [...head, {label: 'Другие', count: tail.reduce((sum, item) => sum + item.count, 0)}];
      }

      const total = rows.reduce((sum, item) => sum + item.count, 0);
      return rows.map((row, index) => ({
        ...row,
        color: row.label === 'Другие' ? this.palette.neutral : colors[index],
        share: total ? Math.round((row.count / total) * 1000) / 10 : 0,
      }));
    },

    sourcesChartOptions() {
      return {
        chart: {type: 'donut', fontFamily: 'inherit', background: 'transparent', animations: {enabled: false}},
        theme: {mode: this.isDark ? 'dark' : 'light'},
        labels: this.sourceRows.map(row => row.label),
        colors: this.sourceRows.map(row => row.color),
        stroke: {width: 2, colors: [this.isDark ? '#262932' : '#ffffff']},
        legend: {position: 'bottom', markers: {radius: 3}, labels: {colors: this.palette.text}},
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(1) + '%',
          style: {fontSize: '11px', fontWeight: 600},
          dropShadow: {enabled: false},
        },
        tooltip: {theme: this.isDark ? 'dark' : 'light'},
        plotOptions: {pie: {donut: {size: '66%'}}},
      };
    },

    sourcesChartSeries() {
      return this.sourceRows.map(row => row.count);
    },

    // ---------------------------------------------- категории
    categoryRows() {
      const rows = (this.categoriesData || []).map(row => ({
        name: row.category_name || 'Без категории',
        count: Number(row.count) || 0,
      })).sort((a, b) => b.count - a.count);

      const max = rows.reduce((peak, row) => Math.max(peak, row.count), 0);
      return rows.map(row => ({...row, percent: max ? Math.round((row.count / max) * 100) : 0}));
    },

    // ---------------------------------------------- команда
    maxProcessed() {
      return (this.teamPerformance || []).reduce((peak, agent) => Math.max(peak, Number(agent.processed) || 0), 0);
    },

    sortedTeamPerformance() {
      const query = this.teamSearch.trim().toLowerCase();
      const list = (this.teamPerformance || [])
          .filter(agent => !query || String(agent.username || '').toLowerCase().includes(query));

      const key = this.teamSortKey;
      const asc = this.teamSortAsc;
      return list.slice().sort((a, b) => {
        let valueA = a[key];
        let valueB = b[key];
        if (valueA === null || valueA === undefined) valueA = -1;
        if (valueB === null || valueB === undefined) valueB = -1;
        if (typeof valueA === 'string' || typeof valueB === 'string') {
          return asc
              ? String(valueA).localeCompare(String(valueB))
              : String(valueB).localeCompare(String(valueA));
        }
        return asc ? valueA - valueB : valueB - valueA;
      });
    },
  },

  mounted() {
    this.detectTheme();
    this.themeObserver = new MutationObserver(this.detectTheme);
    this.themeObserver.observe(document.body, {attributes: true, attributeFilter: ['class']});

    this.loadAll();
    this.loadTypeOptions();
  },

  beforeDestroy() {
    if (this.themeObserver) this.themeObserver.disconnect();
  },

  methods: {
    detectTheme() {
      this.isDark = document.body.classList.contains('dark-only');
    },

    setPeriod(value) {
      if (this.period === value) return;
      this.period = value;
      this.loadAll();
    },

    setGroupBy(value) {
      if (this.groupBy === value) return;
      this.groupBy = value;
      this.loadVolumeChart();
    },

    setCategoryType(value) {
      if (this.categoryTypeId === value) return;
      this.categoryTypeId = value;
      this.loadCategories();
    },

    loadAll() {
      this.loadMetrics();
      this.loadVolumeChart();
      this.loadSources();
      this.loadCategories();
      this.loadTeamPerformance();
      this.updatedAt = new Date().toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
    },

    async loadMetrics() {
      this.metricsLoading = true;
      try {
        const res = await api.ticketsDashboard.getMetrics({period: this.period});
        this.metrics = (res.data && res.data.data) || null;
      } catch (e) {
        this.metrics = null;
        this.reportError('метрики');
      } finally {
        this.metricsLoading = false;
      }
    },

    async loadVolumeChart() {
      this.volumeLoading = true;
      try {
        const res = await api.ticketsDashboard.getVolumeChart({period: this.period, group_by: this.groupBy});
        this.volumeData = (res.data && res.data.data) || [];
      } catch (e) {
        this.volumeData = [];
        this.reportError('динамику обращений');
      } finally {
        this.volumeLoading = false;
      }
    },

    async loadSources() {
      this.sourcesLoading = true;
      try {
        const res = await api.ticketsDashboard.getSources({period: this.period});
        this.sourcesData = (res.data && res.data.data) || [];
      } catch (e) {
        this.sourcesData = [];
        this.reportError('источники');
      } finally {
        this.sourcesLoading = false;
      }
    },

    async loadCategories() {
      this.categoriesLoading = true;
      try {
        const params = {period: this.period};
        if (this.categoryTypeId) params.type_id = this.categoryTypeId;
        const res = await api.ticketsDashboard.getCategories(params);
        this.categoriesData = (res.data && res.data.data) || [];
      } catch (e) {
        this.categoriesData = [];
        this.reportError('категории');
      } finally {
        this.categoriesLoading = false;
      }
    },

    async loadTeamPerformance() {
      this.teamLoading = true;
      try {
        const res = await api.ticketsDashboard.getTeamPerformance({period: this.period});
        this.teamPerformance = (res.data && res.data.data) || [];
      } catch (e) {
        this.teamPerformance = [];
        this.reportError('производительность команды');
      } finally {
        this.teamLoading = false;
      }
    },

    async loadTypeOptions() {
      try {
        const res = await api.tickets.getListTicketTypes();
        const types = ((res.data && res.data.data) || []).map(type => ({value: type.id, text: type.name}));
        this.categoryTypeOptions = [{value: null, text: 'Все типы'}, ...types];
      } catch (e) {
        this.categoryTypeOptions = [{value: null, text: 'Все типы'}];
      }
    },

    reportError(what) {
      this.$toasted.error(`Не удалось загрузить ${what}`, {position: 'top-right', duration: 3000});
    },

    toggleTeamSort(key) {
      if (this.teamSortKey === key) {
        this.teamSortAsc = !this.teamSortAsc;
      } else {
        this.teamSortKey = key;
        this.teamSortAsc = false;
      }
    },

    getSortIcon(key) {
      if (this.teamSortKey !== key) return '↕';
      return this.teamSortAsc ? '↑' : '↓';
    },

    loadPercent(processed) {
      if (!this.maxProcessed) return 0;
      return Math.round((Number(processed) || 0) / this.maxProcessed * 100);
    },

    formatMinutes(value) {
      if (value === null || value === undefined) return '—';
      const minutes = Number(value);
      if (isNaN(minutes)) return '—';
      if (minutes < 60) return minutes.toFixed(1) + ' м';
      const hours = Math.floor(minutes / 60);
      const rest = Math.round(minutes % 60);
      if (hours < 24) return `${hours} ч ${rest} м`;
      const days = Math.floor(hours / 24);
      return `${days} д ${hours % 24} ч`;
    },

    getInitial(name) {
      if (!name) return '?';
      return String(name).charAt(0).toUpperCase();
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/tournament-form.scss";

/* ---------- переключатель периода ---------- */
.td-seg {
  display: inline-flex;
  padding: 3px;
  gap: 3px;
  background: var(--tf-soft);
  border: 1px solid var(--tf-border);
  border-radius: 10px;

  &__btn {
    height: 30px;
    padding: 0 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--tf-muted);
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all .16s ease;

    &:hover { color: var(--tf-primary); }

    &.is-on {
      background: var(--tf-bg);
      color: var(--tf-primary);
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(30, 38, 60, .12);
    }
  }

  &--sm &__btn { height: 26px; padding: 0 10px; font-size: 11.5px; }
}

/* ---------- плитки метрик ---------- */
.td-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.td-metric {
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius);
  box-shadow: var(--tf-shadow);
  padding: 16px 18px;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
  }

  &__icon {
    font-size: 14px;
    color: var(--tf-muted);
  }

  &__value {
    margin-top: 10px;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.1;
    color: var(--tf-text);
    font-variant-numeric: tabular-nums;
  }

  &__sub {
    margin-top: 4px;
    font-size: 11.5px;
    color: var(--tf-muted);
  }

  &--primary &__icon { color: var(--tf-primary); }
  &--ok &__icon { color: var(--tf-ok); }
  &--danger &__icon { color: var(--tf-danger); }
}

/* ---------- сетка карточек ---------- */
.td-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.td-card--wide { grid-column: span 2; }

@media (max-width: 1199px) {
  .td-grid { grid-template-columns: minmax(0, 1fr); }
  .td-card--wide { grid-column: span 1; }
}

.td-head-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tf-card__body--flush { padding: 0; }

/* ---------- состояния графика ---------- */
.td-plot--busy {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.td-empty {
  height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--tf-muted);
  font-size: 13px;

  i { font-size: 26px; opacity: .5; }
}

/* ---------- горизонтальные бары категорий ---------- */
.td-bars {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.td-bar {
  &__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
  }

  &__name {
    font-size: 12.5px;
    color: var(--tf-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__value {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--tf-text);
    font-variant-numeric: tabular-nums;
  }

  &__track {
    height: 8px;
    border-radius: 4px;
    background: var(--tf-soft-2);
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    border-radius: 4px;
    background: var(--tf-primary);
    transition: width .3s ease;
  }
}

/* ---------- таблицы ---------- */
.td-table-wrap { overflow-x: auto; }

.td-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 11px 16px;
    border-bottom: 1px solid var(--tf-border);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--tf-border);
    font-size: 13px;
    color: var(--tf-text);
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0; }

  &--hover tbody tr:hover td { background: var(--tf-soft); }
}

.td-num { text-align: right; font-variant-numeric: tabular-nums; }

.td-th--sort {
  cursor: pointer;
  user-select: none;

  &:hover { color: var(--tf-primary); }
}

.td-sort { opacity: .6; font-size: 10px; }

.td-swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: baseline;
}

/* ---------- сотрудники ---------- */
.td-agent {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    background: var(--tf-primary-soft);
    color: var(--tf-primary);
    font-size: 12px;
    font-weight: 700;
    flex: 0 0 auto;
  }

  &__name { font-weight: 600; }
}

.td-load {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;

  b { font-size: 13px; font-weight: 700; }

  &__track {
    width: 72px;
    height: 6px;
    border-radius: 3px;
    background: var(--tf-soft-2);
    overflow: hidden;
  }

  &__fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--tf-primary);
    opacity: .8;
  }
}

.td-entries {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
  white-space: nowrap;

  b { font-size: 13px; font-weight: 700; }
  b.is-ok { color: var(--tf-ok); }
  b.is-no { color: var(--tf-danger); }
  i { font-style: normal; color: var(--tf-muted); font-size: 11px; }

  &__sep { color: var(--tf-muted); opacity: .6; }
}

/* ---------- поиск в шапке карточки ---------- */
.td-search {
  position: relative;

  i {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: var(--tf-muted);
    pointer-events: none;
  }

  input {
    height: 32px;
    width: 180px;
    padding: 0 12px 0 30px;
    border: 1px solid var(--tf-border);
    border-radius: 9px;
    background: var(--tf-soft);
    color: var(--tf-text);
    font-size: 12.5px;
    outline: none;

    &::placeholder { color: var(--tf-muted); }

    &:focus {
      background: var(--tf-bg);
      border-color: var(--tf-primary);
      box-shadow: 0 0 0 3px var(--tf-primary-soft);
    }

    &::-webkit-search-cancel-button { display: none; }
  }
}

/* ---------- дропдаун типа категорий ---------- */
.td-drop {
  ::v-deep > .btn {
    padding: 0;
    border: 0;
    box-shadow: none !important;
  }

  ::v-deep .td-drop__menu {
    border: 1px solid var(--tf-border);
    border-radius: 12px;
    box-shadow: var(--tf-shadow-lg);
    padding: 6px;

    .dropdown-item {
      border-radius: 8px;
      font-size: 13px;
      padding: 7px 10px;
    }
  }
}

.td-drop__check {
  width: 14px;
  margin-right: 4px;
  color: var(--tf-primary);

  &.is-hidden { visibility: hidden; }
}

/* ---------- скелетоны ---------- */
.td-sk {
  display: block;
  border-radius: 8px;
  background: linear-gradient(90deg, var(--tf-soft) 25%, var(--tf-soft-2) 37%, var(--tf-soft) 63%);
  background-size: 400% 100%;
  animation: td-shimmer 1.3s ease infinite;

  &--value { height: 26px; width: 90px; }
  &--plot { height: 220px; width: 100%; }
}

@keyframes td-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>
